"use client";

import { useId, useState, type FormEvent } from "react";
import { Copy } from "lucide-react";
import { ExplainableSection } from "@/components/ExplainableSection";
import { CodeHighlight } from "@/components/CodeHighlight";
import {
  buildSectionHtml,
  detailParagraphs,
  FIELD_LIMITS,
  validateSection,
  type SectionContent,
} from "./section-html";

const INITIAL: SectionContent = {
  summary:
    "Plane bei einer Werkbank Stellfläche und Arbeitsraum getrennt ein; ohne Raum zum Arbeiten ist auch eine passende Tischplatte keine brauchbare Lösung.",
  question: "Wie unterscheiden sich Stellfläche und Arbeitsraum?",
  detail:
    "Die Stellfläche beschreibt, welchen Teil des Bodens die Werkbank belegt. Der Arbeitsraum umfasst zusätzlich die Bereiche, in denen du stehst, Werkstücke bewegst und Werkzeug erreichst.\n\nMarkiere den geplanten Standort am Boden und spiele deine typischen Arbeitsschritte durch. Ob der Platz reicht, hängt von deinen Werkstücken und Tätigkeiten ab; aus der Tischgröße allein lässt sich das nicht ableiten.",
};

const LABELS = {
  summary: "Kernaussage – bleibt immer sichtbar",
  question: "Welche Frage beantwortet die Vertiefung?",
  detail: "Vertiefung – Leerzeilen trennen Absätze",
};

export default function ReadingDepthPlayground() {
  const id = useId();
  const [draft, setDraft] = useState<SectionContent>(INITIAL);
  const [applied, setApplied] = useState<SectionContent>(INITIAL);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState(
    "Beispiel geladen; deine Texte bleiben in diesem Browser-Tab.",
  );
  const dirty = (Object.keys(draft) as (keyof SectionContent)[]).some(
    (field) => draft[field] !== applied[field],
  );
  const html = buildSectionHtml(applied);

  function apply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validation = validateSection(draft);
    setError(validation);
    if (validation) return;
    setApplied({ ...draft });
    setRevision((previous) => previous + 1);
    setStatus(
      "Vorschau und HTML aktualisiert; öffne die Frage, um die Vertiefung zu lesen.",
    );
  }

  async function copyHtml() {
    try {
      await navigator.clipboard.writeText(html);
      setError(null);
      setStatus("HTML der aktuellen Vorschau kopiert.");
    } catch {
      setError(
        "Kopieren nicht möglich; markiere und kopiere den HTML-Code manuell.",
      );
    }
  }

  return (
    <div className="min-h-[620px] space-y-6">
      <form onSubmit={apply} noValidate className="space-y-4">
        {(["summary", "question", "detail"] as const).map((field) => (
          <div key={field}>
            <label
              htmlFor={`${id}-${field}`}
              className="mb-2 block text-sm font-semibold"
            >
              {LABELS[field]}
            </label>
            <textarea
              id={`${id}-${field}`}
              rows={field === "detail" ? 5 : 2}
              maxLength={FIELD_LIMITS[field]}
              value={draft[field]}
              aria-describedby={`${id}-${field}-limit`}
              className="w-full resize-y rounded-xl border border-neutral-300 bg-white p-3 text-base leading-relaxed text-neutral-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
              onChange={(event) => {
                setDraft((previous) => ({
                  ...previous,
                  [field]: event.target.value,
                }));
                setError(null);
              }}
            />
            <p
              id={`${id}-${field}-limit`}
              className="mt-1! mb-0! text-xs text-neutral-600 dark:text-neutral-400"
            >
              {draft[field].length} / {FIELD_LIMITS[field]} Zeichen
            </p>
          </div>
        ))}
        <button
          type="submit"
          className="min-h-11 rounded-lg bg-rose-700 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-800 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-rose-500"
        >
          Vorschau aktualisieren
        </button>
      </form>
      <div className="min-h-14 text-sm" aria-live="polite" aria-atomic="true">
        {error ? (
          <p role="alert" className="m-0! text-red-700 dark:text-red-300">
            {error}
          </p>
        ) : (
          <p className="m-0! text-neutral-600 dark:text-neutral-400">
            {dirty
              ? "Ungespeicherte Eingaben: Vorschau und Export zeigen noch die letzte übernommene Fassung."
              : status}
          </p>
        )}
      </div>
      <div
        className="min-h-48 border-y border-neutral-200 py-4 dark:border-neutral-800"
        aria-label="Vorschau des Textabschnitts"
      >
        <p className="m-0! text-xs font-semibold tracking-wide text-neutral-600 uppercase dark:text-neutral-400">
          Lesevorschau
        </p>
        <ExplainableSection
          key={revision}
          summary={applied.summary}
          question={applied.question}
        >
          {detailParagraphs(applied.detail).map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </ExplainableSection>
      </div>
      <details>
        <summary className="min-h-11 cursor-pointer py-2 font-semibold text-rose-700 focus-visible:outline-2 focus-visible:outline-rose-500 dark:text-rose-300">
          Statisches HTML übernehmen
        </summary>
        <div className="mt-3 overflow-hidden rounded-xl border border-neutral-800 bg-zinc-950">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-800 bg-zinc-900 px-4 py-2 text-sm text-zinc-200">
            <span className="font-mono">section.html</span>
            <button
              type="button"
              onClick={copyHtml}
              className="inline-flex min-h-11 items-center gap-2 rounded-md px-3 py-2 hover:bg-zinc-800 focus-visible:outline-2 focus-visible:outline-rose-400"
            >
              <Copy aria-hidden="true" className="h-4 w-4" /> HTML kopieren
            </button>
          </div>
          <CodeHighlight code={html} language="html" />
        </div>
      </details>
    </div>
  );
}
